import * as React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  makeStyles,
  Select,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import * as styles from './build-segment.module.scss';
import { SearchBar, SecondaryButton, TertiaryButton } from 'kagr-ui';
import AttributeMenuOption from './attribute-menu-option';
import { Clear } from '@material-ui/icons';
import { SegmentAttributeOptions, SelectedAttributeOption } from '../../../models/segments';
import { MultiselectChip } from './multiselect-chip';
import { useBuildSegment } from './useBuildSegment';

interface Props {
  attribute: SegmentAttributeOptions;
  selectedAttribute: SelectedAttributeOption;
  position: number[];
  exclude?: boolean;
  applyTransactional?: (checkedOptions: string[]) => void;
  deleteTransactionalChip?: (chip: string) => void;
  attributeIndex?: number;
}

const useStyles = makeStyles((theme) => ({
  dialog: {
    [theme.breakpoints.up('sm')]: {
      '& .MuiDialog-paperWidthSm': {
        maxWidth: '640px'
      }
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  dialogActions: {
    justifyContent: 'space-between',
    height: 57,
    padding: '0 24px'
  },
  searchBar: {
    width: 180
  }
}));

/**
 * Select menu and accompanying modal dialog for filtering a single attribute of filterType: options when building a segment
 */
const AttributeFilterOptions = ({
  attribute,
  selectedAttribute,
  position,
  exclude,
  applyTransactional,
  deleteTransactionalChip,
  attributeIndex
}: Props) => {
  const overrides = useStyles();
  const { dispatch } = useBuildSegment(exclude);
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  /** Currently checked filters in the filter dialog, not yet applied to select menu */
  const checkedFilters = React.useRef<string[]>(selectedAttribute.options);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const [searchValue, setSearchValue] = React.useState<string>('');

  /** Only query for filter values if user opens the filter menu dialog, don't prefetch */
  React.useEffect(() => {
    if (!dialogOpen) {
      setSearchValue('');
    }
  }, [dialogOpen]);

  React.useEffect(() => {
    checkedFilters.current = selectedAttribute.options;
  }, [selectedAttribute.options]);

  /** Reset to previous filters on cancel */
  const handleCancel = () => {
    checkedFilters.current = selectedAttribute.options;
    setDialogOpen(false);
  };

  const handleApply = () => {
    if (!!applyTransactional) {
      applyTransactional(checkedFilters.current);
    } else {
      dispatch({
        type: 'updateAttribute',
        attribute: { ...selectedAttribute, options: checkedFilters.current },
        position,
        attributeIndex: attributeIndex
      });
    }
    setDialogOpen(false);
  };

  /** Callback passed to checkboxes inputs which manage their own checked state */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      checkedFilters.current = [...checkedFilters.current, event.target.value];
    } else {
      checkedFilters.current = checkedFilters.current.filter((filterName) => filterName != event.target.value);
    }
  };

  const handleDeleteChip = (option: string) => {
    if (!!deleteTransactionalChip) {
      deleteTransactionalChip(option);
    } else {
      dispatch({
        type: 'updateAttribute',
        attribute: {
          ...selectedAttribute,
          options: selectedAttribute.options.filter((filterName) => filterName !== option)
        },
        position,
        attributeIndex: attributeIndex
      });
    }
  };

  /** Function passed to filter select `renderValue` prop: conditionally render the custom content of the filter multi-select menu */
  const renderFilterValues = (value: unknown) => {
    if (attribute.filters.length === 0) {
      return <span className={styles.filterText}>There are no filters to apply</span>;
    }

    if (selectedAttribute.options.length === 0 || selectedAttribute.options.length === attribute.filters.length) {
      return <span className={styles.filterText}>All - click for additional filtering</span>;
    }

    return (
      <div className={styles.filterChips}>
        {(value as string[]).map((valueName) => {
          const label = valueName.trim() === '' ? <span className={styles.blankOption}>blank</span> : valueName;
          return (
            <MultiselectChip
              key={valueName}
              label={label}
              size="small"
              deleteIcon={<Clear onMouseDown={(event) => event.stopPropagation()} aria-label="delete filter" />}
              onDelete={() => handleDeleteChip(valueName)}
            />
          );
        })}
      </div>
    );
  };

  return (
    <React.Fragment>
      <Select
        id={`filter-attribute-${attribute.id}`}
        multiple={true}
        disabled={attribute.filters.length === 0}
        value={selectedAttribute.options}
        open={false}
        onOpen={() => {
          setDialogOpen(true);
        }}
        renderValue={renderFilterValues}
        displayEmpty={true}
      />
      <Dialog
        open={dialogOpen}
        maxWidth="sm"
        scroll="paper"
        fullScreen={fullScreen}
        className={overrides.dialog}
        aria-labelledby="select-attribute-dialog-title"
      >
        <DialogTitle className={overrides.dialogTitle} id="select-attribute-dialog-title" disableTypography>
          <h3 className={styles.dialogTitle}>Select Filters</h3>
          <SearchBar
            value={searchValue}
            updateValue={setSearchValue}
            placeholder="Search Filters"
            allowSubmitButton={false}
            showClearButton
            className={overrides.searchBar}
          />
        </DialogTitle>
        <DialogContent dividers>
          <FormGroup>
            {attribute.filters.map((filterOption) => (
              <AttributeMenuOption
                key={`${filterOption.value}`}
                id={filterOption.value}
                name={String(filterOption.value)}
                secondaryText={filterOption.customers.toLocaleString()}
                initialChecked={selectedAttribute.options.includes(String(filterOption.value))}
                onChange={handleChange}
                hidden={
                  searchValue.length >= 3 &&
                  !String(filterOption.value).toLowerCase().includes(searchValue.toLowerCase())
                }
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions className={overrides.dialogActions}>
          <TertiaryButton text="Cancel" title="Cancel" component="button" handleClick={handleCancel} />
          <SecondaryButton text="Apply" title="Apply" handleClick={handleApply} />
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default AttributeFilterOptions;
